// Client-side derivation helpers: GameDetail -> GameRecord (with inferred
// resume points) and memoized replay so board rendering / cursor scrubbing
// never re-replays the same prefix twice.

import type { GameDetail, Vertex, Color } from '../shared/types.ts';
import { isPass, isResign } from '../shared/go/coords.ts';
import { replay, type GameRecord, type ReplayState } from '../shared/go/game.ts';

/**
 * Build a replayable GameRecord from a server GameDetail. Resume points are
 * inferred: whenever two consecutive passes are followed by more moves, play
 * must have been resumed at that count (the server keeps pass moves in the
 * record on resume).
 */
export function recordFromDetail(detail: GameDetail): GameRecord {
  const moves = detail.moves.map((m) => ({ color: m.color, vertex: m.vertex }));
  const resumePoints: number[] = [];
  let passes = 0;
  for (let i = 0; i < moves.length; i++) {
    const v = moves[i]!.vertex;
    if (isResign(v)) break;
    passes = isPass(v) ? passes + 1 : 0;
    if (passes >= 2 && i + 1 < moves.length) {
      resumePoints.push(i + 1);
      passes = 0;
    }
  }
  return {
    boardSize: detail.game.boardSize,
    komi: detail.game.komi,
    mode: detail.game.mode,
    humanColor: detail.game.humanColor,
    moves,
    resumePoints,
  };
}

const replayCache = new WeakMap<GameRecord, Map<number, ReplayState>>();

/** Memoized replay of the first `upto` moves of a (frozen) record. */
export function replayAt(record: GameRecord, upto?: number): ReplayState {
  const key = Math.min(upto ?? record.moves.length, record.moves.length);
  let perRecord = replayCache.get(record);
  if (!perRecord) {
    perRecord = new Map();
    replayCache.set(record, perRecord);
  }
  let state = perRecord.get(key);
  if (!state) {
    state = replay(record, key);
    perRecord.set(key, state);
  }
  return state;
}

export function describeMove(color: Color, vertex: Vertex, captured: number): string {
  const who = color === 'B' ? 'Black' : 'White';
  if (isPass(vertex)) return `${who} passes`;
  if (isResign(vertex)) return `${who} resigns`;
  const caps =
    captured > 0 ? `, capturing ${captured} ${captured === 1 ? 'stone' : 'stones'}` : '';
  return `${who} plays ${vertex}${caps}`;
}
